import { Button, ButtonGroup, CloseButton, HStack, Icon, Spinner, Text } from '@chakra-ui/react';
import { Pencil } from 'lucide-react';
import { Trans, Plural } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { getLanguage } from '@duke-hq/shared-constants/courseTranslationLanguages';
import { iconSize } from '@duke-hq/ui/constants/scalable';
import { CanvasFloatingBar } from '@duke-hq/app-admin-web/components/ReactFlow/CanvasFloatingBar';
import { testIds } from './testIds';

export function CanvasLanguageBar({
  language,
  showingEnglish,
  isLoading = false,
  isError = false,
  editedCount = 0,
  onShowEnglish,
  onShowTarget,
  onEditTranslation,
  onClose,
  position = 'absolute',
}: {
  language: string;
  showingEnglish: boolean;
  isLoading?: boolean;
  isError?: boolean;
  // Segments left in English. When > 0 (and showing the target), the bar notes
  // the count. Default 0 keeps the canvas caller (no partial preview) unaffected.
  editedCount?: number;
  onShowEnglish: () => void;
  onShowTarget: () => void;
  onEditTranslation?: () => void;
  onClose: () => void;
  // `absolute` on the canvas; `static` on the unit guide page, where a sticky
  // wrapper owns the scroll-follow (see the positioning block below).
  position?: 'absolute' | 'static';
}) {
  const endonym = getLanguage(language)?.endonym ?? language;
  const englishName = getLanguage(language)?.englishName ?? language;

  return (
    <CanvasFloatingBar
      position={position}
      barTestId={testIds.languageBar}
      gripTestId={testIds.languageBarDrag}
    >
      <HStack spacing={2}>
        <ButtonGroup size="xs" isAttached variant="secondary">
          <Button
            isActive={showingEnglish}
            aria-pressed={showingEnglish}
            onClick={onShowEnglish}
            data-testid={testIds.languageBarSwitch}
          >
            <Trans>English</Trans>
          </Button>
          <Button isActive={!showingEnglish} aria-pressed={!showingEnglish} onClick={onShowTarget}>
            {endonym}
          </Button>
        </ButtonGroup>
        {!showingEnglish && isError ? (
          <Text fontSize="xs" color="red.500" data-testid={testIds.languageBarReadOnly}>
            <Trans>Couldn't load the translation</Trans>
          </Text>
        ) : !showingEnglish && isLoading ? (
          <HStack spacing={1.5}>
            <Spinner size="xs" color="fg.muted" />
            <Text fontSize="xs" color="fg.muted" data-testid={testIds.languageBarReadOnly}>
              <Trans>Loading translation…</Trans>
            </Text>
          </HStack>
        ) : !showingEnglish && editedCount > 0 ? (
          <Text fontSize="xs" color="fg.muted" data-testid={testIds.languageBarReadOnly}>
            <Trans>
              Published translation ·{' '}
              <Plural
                value={editedCount}
                one="# part not yet translated"
                other="# parts not yet translated"
              />
            </Trans>
          </Text>
        ) : (
          <Text fontSize="xs" color="fg.muted" data-testid={testIds.languageBarReadOnly}>
            {showingEnglish ? (
              <Trans>Read-only preview</Trans>
            ) : (
              <Trans>Read-only preview in {englishName}</Trans>
            )}
          </Text>
        )}
        {onEditTranslation != null ? (
          <Button
            size="xs"
            variant="secondary"
            leftIcon={<Icon as={Pencil} boxSize={iconSize.xs} />}
            onClick={onEditTranslation}
            data-testid={testIds.languageBarEdit}
          >
            <Trans>Edit translation</Trans>
          </Button>
        ) : null}
        <CloseButton
          size="sm"
          onClick={onClose}
          aria-label={t`Exit translation preview`}
          data-testid={testIds.languageBarClose}
        />
      </HStack>
    </CanvasFloatingBar>
  );
}
